import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import React from "react";

export default function DiscoveredAssetsTable({ data, export: isExport }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // If export is true, use the simplified version for better PDF export
  if (isExport) {
    return (
      <div
        style={{
          boxShadow: "0px 3px 6px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          padding: "1px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderSpacing: "0",
            borderCollapse: "collapse",
            backgroundColor: "#fff",
            tableLayout: "fixed",
          }}
        >
          {/* Table Header */}
          <thead>
            <tr>
              <th
                style={{
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  color: "#333",
                  padding: "8px 6px",
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  width: "20%",
                }}
              >
                Category
              </th>
              <th
                style={{
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  color: "#333",
                  padding: "8px 6px",
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  width: "30%",
                }}
              >
                Asset
              </th>
              <th
                style={{
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  color: "#333",
                  padding: "8px 6px",
                  textAlign: "right",
                  borderBottom: "1px solid #ddd",
                  width: "20%",
                }}
              >
                Asset Value
              </th>
              <th
                style={{
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  color: "#333",
                  padding: "8px 6px",
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  width: "30%",
                }}
              >
                Associated Subdomains
              </th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {data.map((item, index) => (
              <React.Fragment key={index}>
                {item.discovered_assets.map((asset, assetIndex) => {
                  const isLastRow =
                    assetIndex === item.discovered_assets.length - 1;

                  return (
                    <tr
                      key={`${index}-${assetIndex}`}
                      style={{
                        borderBottom: isLastRow ? "1px solid #ddd" : "none",
                      }}
                    >
                      {assetIndex === 0 ? (
                        <td
                          rowSpan={item.discovered_assets.length}
                          style={{
                            fontWeight: "bold",
                            color: "#333",
                            verticalAlign: "top",
                            padding: "8px 6px",
                            fontSize: "0.75rem",
                          }}
                        >
                          {item.category}
                        </td>
                      ) : null}

                      <td
                        style={{
                          padding: "6px",
                          color: "#444",
                          fontSize: "0.75rem",
                        }}
                      >
                        {asset.asset}
                      </td>
                      <td
                        style={{
                          padding: "6px",
                          color: "#444",
                          textAlign: "right",
                          fontSize: "0.75rem",
                        }}
                      >
                        {asset.asset_value}
                      </td>
                      <td
                        style={{
                          padding: "6px",
                          color: "#666",
                          fontSize: "0.75rem",
                        }}
                      >
                        {asset.associated_subdomains.length > 0 ? (
                          asset.associated_subdomains.join(", ")
                        ) : (
                          <span
                            style={{
                              fontStyle: "italic",
                              color: "#999",
                              fontSize: "0.75rem",
                            }}
                          >
                            Empty
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Mobile-friendly vertical layout
  if (isSmallScreen) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {data.map((item, index) => (
          <Paper key={index} elevation={2} sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {item.category}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {item.discovered_assets.map((asset, assetIndex) => (
                <Box
                  key={assetIndex}
                  sx={{
                    borderBottom:
                      assetIndex < item.discovered_assets.length - 1
                        ? "1px solid #eee"
                        : "none",
                    pb: assetIndex < item.discovered_assets.length - 1 ? 2 : 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      Asset:
                    </Typography>
                    <Typography variant="caption">{asset.asset}</Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      Asset Value:
                    </Typography>
                    <Typography variant="caption">
                      {asset.asset_value}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      Associated Subdomains:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          asset.associated_subdomains.length > 0
                            ? "inherit"
                            : "text.disabled",
                        fontStyle:
                          asset.associated_subdomains.length > 0
                            ? "normal"
                            : "italic",
                        wordBreak: "break-word",
                      }}
                    >
                      {asset.associated_subdomains.length > 0
                        ? asset.associated_subdomains.join(", ")
                        : "Empty"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Normal desktop display version with Material UI components
  return (
    <TableContainer
      component={Paper}
      style={{ boxShadow: "0px 3px 6px rgba(0,0,0,0.1)" }}
    >
      <Table
        style={{
          width: "100%",
          borderSpacing: "0px",
          borderCollapse: "collapse",
          backgroundColor: "#fff",
        }}
      >
        {/* Table Header */}
        <TableHead>
          <TableRow
            style={{
              backgroundColor: "#f4f6f9",
              border: "2px solid #bbb",
            }}
          >
            <TableCell
              style={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                color: "#333",
                border: "1px solid #bbb",
                width: "20%",
                padding: "8px",
              }}
            >
              Category
            </TableCell>
            <TableCell
              style={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                color: "#333",
                border: "1px solid #bbb",
                width: "30%",
                padding: "8px",
              }}
            >
              Asset
            </TableCell>
            <TableCell
              align="right"
              style={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                color: "#333",
                border: "1px solid #bbb",
                width: "20%",
                padding: "8px",
              }}
            >
              Asset Value
            </TableCell>
            <TableCell
              style={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                color: "#333",
                border: "1px solid #bbb",
                width: "30%",
                padding: "8px",
              }}
            >
              Associated Subdomains
            </TableCell>
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {data.map((item, index) => (
            <React.Fragment key={index}>
              <TableRow
                style={{
                  border: "1px solid #999",
                }}
              >
                <TableCell
                  rowSpan={item.discovered_assets.length}
                  style={{
                    fontWeight: "bold",
                    color: "#333",
                    verticalAlign: "top",
                    padding: "8px",
                    fontSize: "0.75rem",
                    border: "1px solid #999",
                  }}
                >
                  {item.category}
                </TableCell>

                {item.discovered_assets.length > 0 && (
                  <>
                    <TableCell
                      style={{
                        padding: "6px",
                        color: "#444",
                        border: "1px solid #999",
                        fontSize: "0.75rem",
                      }}
                    >
                      {item.discovered_assets[0].asset}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        padding: "6px",
                        color: "#444",
                        border: "1px solid #999",
                        fontSize: "0.75rem",
                      }}
                    >
                      {item.discovered_assets[0].asset_value}
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "6px",
                        color: "#666",
                        border: "1px solid #999",
                        fontSize: "0.75rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.discovered_assets[0].associated_subdomains.length >
                      0 ? (
                        item.discovered_assets[0].associated_subdomains.join(
                          ", "
                        )
                      ) : (
                        <span
                          style={{
                            fontStyle: "italic",
                            color: "#999",
                            fontSize: "0.75rem",
                          }}
                        >
                          Empty
                        </span>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>

              {item.discovered_assets.slice(1).map((asset, assetIndex) => (
                <TableRow
                  key={assetIndex}
                  style={{
                    border: "1px solid #999",
                  }}
                >
                  <TableCell
                    style={{
                      padding: "6px",
                      color: "#444",
                      border: "1px solid #999",
                      fontSize: "0.75rem",
                    }}
                  >
                    {asset.asset}
                  </TableCell>
                  <TableCell
                    align="right"
                    style={{
                      padding: "6px",
                      color: "#444",
                      border: "1px solid #999",
                      fontSize: "0.75rem",
                    }}
                  >
                    {asset.asset_value}
                  </TableCell>
                  <TableCell
                    style={{
                      padding: "6px",
                      color: "#666",
                      border: "1px solid #999",
                      fontSize: "0.75rem",
                      wordBreak: "break-word",
                    }}
                  >
                    {asset.associated_subdomains.length > 0 ? (
                      asset.associated_subdomains.join(", ")
                    ) : (
                      <span
                        style={{
                          fontStyle: "italic",
                          color: "#999",
                          fontSize: "0.75rem",
                        }}
                      >
                        Empty
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
